import { PAGES_CACHE_NAME } from '@serwist/turbopack/worker';
import {
  CacheFirst,
  ExpirationPlugin,
  NetworkFirst,
  NetworkOnly,
  RangeRequestsPlugin,
  type RuntimeCaching,
  StaleWhileRevalidate,
} from 'serwist';

const getClientTypeFromUA = (request: Request): 'pwashell' | 'web' => {
  const ua = request.headers.get('user-agent')?.toLowerCase() ?? '';
  return ua.includes('pwashell') ? 'pwashell' : 'web';
};

const appendVaryUserAgent = (response: Response): Response => {
  if (response.type !== 'default') return response;
  const vary = response.headers.get('Vary');
  const hasUserAgent = vary
    ?.split(',')
    .map((v) => v.trim().toLowerCase())
    .includes('user-agent');
  if (hasUserAgent) return response;

  const headers = new Headers(response.headers);
  headers.set('Vary', vary ? `${vary}, User-Agent` : 'User-Agent');

  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
};

const runtimeCaching: RuntimeCaching[] = [
  // Adapted from https://github.com/serwist/serwist/blob/main/packages/next/src/index.worker.ts
  /* ---------- Fonts ---------- */
  {
    matcher: /^https:\/\/fonts\.(?:gstatic)\.com\/.*/i,
    handler: new CacheFirst({
      cacheName: 'google-fonts-webfonts',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 4,
          maxAgeSeconds: 365 * 24 * 60 * 60, // 365 days
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  {
    matcher: /^https:\/\/fonts\.(?:googleapis)\.com\/.*/i,
    handler: new StaleWhileRevalidate({
      cacheName: 'google-fonts-stylesheets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 4,
          maxAgeSeconds: 7 * 24 * 60 * 60, // 7 days
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  {
    matcher: /\.(?:eot|otf|ttc|ttf|woff|woff2|font.css)$/i,
    handler: new StaleWhileRevalidate({
      cacheName: 'static-font-assets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 4,
          maxAgeSeconds: 7 * 24 * 60 * 60, // 7 days
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  /* ---------- Images ---------- */
  {
    matcher: /\.(?:jpg|jpeg|gif|png|svg|ico|webp)$/i,
    handler: new StaleWhileRevalidate({
      cacheName: 'static-image-assets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 64,
          maxAgeSeconds: 30 * 24 * 60 * 60, // 30 days
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  {
    matcher: /\/_next\/image\?url=.+$/i,
    handler: new StaleWhileRevalidate({
      cacheName: 'next-image',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 64,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  {
    matcher: ({ request }) => request.destination === 'image',
    handler: new CacheFirst({
      cacheName: 'image-assets',
      plugins: [
        // keep at most 120 images / 30 days to avoid removing js assets if iOS hits the 50 MB shared quota
        new ExpirationPlugin({
          maxEntries: 120,
          maxAgeSeconds: 30 * 24 * 60 * 60,
          purgeOnQuotaError: true,
        }),
      ],
    }),
  },
  /* ---------- Google Maps tiles / sprites ---------- */
  {
    matcher: /^https:\/\/maps\.googleapis\.com\/*/i,
    handler: new CacheFirst({
      cacheName: 'google-maps-assets',
      plugins: [new ExpirationPlugin({ maxEntries: 4, maxAgeSeconds: 15 * 60 })],
    }),
  },
  /* ---------- Navigation ---------- */
  {
    matcher: /\/_next\/data\/.+\/.+\.json/i, // Lack of final $ allows matching in presence of query params, e.g. when including search filters
    handler: new CacheFirst({
      cacheName: 'next-data',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32, // Note: each entry approx 500 kB => theoretical size ~ 16 MB
          maxAgeSeconds: 15 * 60, // 15 minutes
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  /* ---------- Static assets ---------- */
  {
    matcher: /\/_next\/static.+\.js$/i,
    handler: new CacheFirst({
      cacheName: 'next-static-js-assets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 64,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  {
    matcher: /\.(?:mp3|wav|ogg)$/i,
    handler: new CacheFirst({
      cacheName: 'static-audio-assets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
          maxAgeFrom: 'last-used',
        }),
        new RangeRequestsPlugin(),
      ],
    }),
  },
  {
    matcher: /\.(?:mp4|webm)$/i,
    handler: new CacheFirst({
      cacheName: 'static-video-assets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
          maxAgeFrom: 'last-used',
        }),
        new RangeRequestsPlugin(),
      ],
    }),
  },
  {
    matcher: /\.(?:js)$/i,
    handler: new StaleWhileRevalidate({
      cacheName: 'static-js-assets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 48,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  {
    matcher: /\.(?:css|less)$/i,
    handler: new StaleWhileRevalidate({
      cacheName: 'static-style-assets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  {
    matcher: /\.(?:json|xml|csv)$/i,
    handler: new NetworkFirst({
      cacheName: 'static-data-assets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
          maxAgeFrom: 'last-used',
        }),
      ],
    }),
  },
  /* ---------- API ---------- */
  {
    // Exclude /api/auth/* to fix auth callback
    // https://github.com/serwist/serwist/discussions/28
    matcher: /\/api\/auth\/.*/,
    handler: new NetworkOnly({
      networkTimeoutSeconds: 10, // fallback to cache if API does not response within 10 seconds
    }),
  },
  {
    matcher: ({ sameOrigin, url: { pathname } }) => sameOrigin && pathname.startsWith('/api/'),
    method: 'GET',
    handler: new NetworkFirst({
      cacheName: 'apis',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 16,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
          maxAgeFrom: 'last-used',
        }),
      ],
      networkTimeoutSeconds: 10, // fallback to cache if API does not response within 10 seconds
    }),
  },
  {
    matcher: ({ request, url: { pathname }, sameOrigin }) =>
      request.headers.get('RSC') === '1' &&
      request.headers.get('Next-Router-Prefetch') === '1' &&
      sameOrigin &&
      !pathname.startsWith('/api/'),
    handler: new NetworkFirst({
      cacheName: PAGES_CACHE_NAME.rscPrefetch,
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
        }),
      ],
    }),
  },
  {
    matcher: ({ request, url: { pathname }, sameOrigin }) =>
      request.headers.get('RSC') === '1' && sameOrigin && !pathname.startsWith('/api/'),
    handler: new NetworkFirst({
      cacheName: PAGES_CACHE_NAME.rsc,
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
        }),
      ],
    }),
  },
  {
    matcher: ({ request, url: { pathname }, sameOrigin }) =>
      request.headers.get('Content-Type')?.includes('text/html') && sameOrigin && !pathname.startsWith('/api/'),
    handler: async ({ event, request }) => {
      const clientType = getClientTypeFromUA(request);
      const cacheName = `${PAGES_CACHE_NAME.html}-${clientType}`;
      const handler = new NetworkFirst({
        cacheName,
        plugins: [
          new ExpirationPlugin({
            maxEntries: 32,
            maxAgeSeconds: 24 * 60 * 60, // 24 hours
          }),
        ],
      });

      const response = await handler.handle({ event, request });
      return appendVaryUserAgent(response);
    },
  },
  {
    matcher: ({ url: { pathname }, sameOrigin }) => sameOrigin && !pathname.startsWith('/api/'),
    handler: new NetworkFirst({
      cacheName: 'others',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32,
          maxAgeSeconds: 24 * 60 * 60, // 24 hours
        }),
      ],
    }),
  },
  {
    matcher: ({ sameOrigin }) => !sameOrigin,
    handler: new NetworkFirst({
      cacheName: 'cross-origin',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32,
          maxAgeSeconds: 60 * 60, // 1 hour
        }),
      ],
      networkTimeoutSeconds: 10,
    }),
  },
  /* ---------- Fallback ---------- */
  {
    matcher: /.*/i,
    method: 'GET',
    handler: new NetworkOnly(),
  },
];

export default runtimeCaching;
